/* src/render/materials.ts — one shared material per layer type.
 *
 * Colour is baked into a vertex attribute by the geometry worker, so a whole layer across a
 * whole tile draws in one call regardless of how many distinct building or landuse colours it
 * contains. Draw calls scale with (visible tiles × layers), not with feature count — the
 * difference between ~30 draw calls and ~20,000 (PLAN.md Part 2 §4).
 *
 * Facade variety works the same way. Several window styles live in ONE texture array; each
 * building carries a per-vertex layer index (`aTexLayer`) picking its style. So four distinct
 * facades cost one texture bind and zero extra draw calls, instead of one mesh per style.
 */

import {
  DataArrayTexture,
  DataTexture,
  DoubleSide,
  FrontSide,
  LinearFilter,
  LinearMipmapLinearFilter,
  type Material,
  MeshLambertMaterial,
  RepeatWrapping,
  RGBAFormat,
  SRGBColorSpace,
  type Texture,
  type WebGLProgramParametersWithUniforms,
} from 'three';
import { FACADE_STYLE_COUNT } from '../workers/protocol.ts';

export interface LayerMaterials {
  readonly buildings: Material;
  readonly roofs: Material;
  readonly roads: Material;
  readonly water: Material;
  readonly landuse: Material;
  readonly trees: Material;
  /**
   * Hide one building (walls and roof) by OSM way id, or null to show everything. Used by the
   * interior's window view: the city is rendered from inside a building whose own exterior
   * would otherwise wrap the camera. The id is compared modulo 2^24 — see tileMesh.ts.
   */
  setHiddenFeature: (osmWayId: number | null) => void;
  dispose: () => void;
}

/* ── Facade styles ────────────────────────────────────────────────────────────────────
 * Each style is one storey cell — a single window bay of a brick building, matching Capital
 * Rift's punched-window look. The cell is drawn around a NEUTRAL grey (0.5): grey lets the
 * per-vertex brick colour show unchanged, darker pixels recess to shadowed glass, lighter
 * pixels lift to pale stone trim (sills, lintels, frames). The building shader blends the cell
 * over the brick with an *overlay* (§ makeBuildingMaterial), which is what lets a light frame
 * read as light stone rather than just a hole. Faint horizontal courses give the wall its
 * subtle brick texture. The wall UVs tile one cell per storey vertically and per ~3.2 m
 * horizontally, so a cell is one bay.
 */
const CELL = 128;
const WALL = '#808080'; // neutral: brick shows through untouched

type Draw = (ctx: CanvasRenderingContext2D) => void;

/**
 * The shared base under every style: neutral brick, then the pale string course that divides
 * one storey from the next. Those horizontal bands running the full width of the facade are the
 * single most recognisable thing about the reference's buildings, so every cell carries one at
 * its floor line, with a shadow under it to give the band some relief.
 */
function brickBase(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = WALL;
  ctx.fillRect(0, 0, CELL, CELL);

  // Brick coursing: alternate rows offset by half a brick, mortar darker than the face.
  ctx.fillStyle = 'rgba(0,0,0,0.055)';
  for (let y = 6; y < CELL; y += 7) ctx.fillRect(0, y, CELL, 1);
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  for (let y = 5; y < CELL; y += 7) ctx.fillRect(0, y, CELL, 1);
  ctx.fillStyle = 'rgba(0,0,0,0.035)'; // vertical perpends, staggered per course
  for (let row = 0, y = 6; y < CELL; y += 7, row += 1) {
    for (let x = (row % 2) * 8; x < CELL; x += 16) ctx.fillRect(x, y - 6, 1, 6);
  }

  // String course at the floor line: bright band, thin shadow beneath.
  ctx.fillStyle = '#ded7c8';
  ctx.fillRect(0, 0, CELL, 7);
  ctx.fillStyle = '#efe9dc'; // sunlit top edge of the band
  ctx.fillRect(0, 0, CELL, 3);
  ctx.fillStyle = 'rgba(0,0,0,0.16)'; // shadow the band casts on the brick below
  ctx.fillRect(0, 7, CELL, 3);
}

/**
 * A punched window with pale stone trim and recessed glass — the Capital Rift unit. `pane`
 * draws the glass; everything else is the light surround, sill and lintel that frame it.
 */
function punchedWindow(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  pane: (gx: number, gy: number, gw: number, gh: number) => void,
): void {
  ctx.fillStyle = 'rgba(0,0,0,0.14)'; // reveal shadow around the opening
  ctx.fillRect(x - 3, y - 2, w + 6, h + 8);
  ctx.fillStyle = '#d6d0c3'; // pale stone surround
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = '#e8e2d5'; // sunlit left jamb
  ctx.fillRect(x, y, 2, h);
  pane(x + 5, y + 5, w - 10, h - 10);
  ctx.fillStyle = '#efe9dc'; // bright projecting sill
  ctx.fillRect(x - 4, y + h - 1, w + 8, 5);
  ctx.fillStyle = 'rgba(0,0,0,0.18)'; // shadow under the sill
  ctx.fillRect(x - 4, y + h + 4, w + 8, 2);
  ctx.fillStyle = '#e2dbcd'; // lintel over the head
  ctx.fillRect(x - 3, y - 4, w + 6, 4);
}

/** Dark cool glass with a lighter reflection across the top — reads as a shadowed pane. */
function darkGlass(ctx: CanvasRenderingContext2D) {
  return (gx: number, gy: number, gw: number, gh: number): void => {
    ctx.fillStyle = '#3f4b55';
    ctx.fillRect(gx, gy, gw, gh);
    ctx.fillStyle = '#5c6a76'; // sky reflection across the upper pane
    ctx.fillRect(gx, gy, gw, Math.ceil(gh * 0.4));
    ctx.fillStyle = '#6f7d88'; // bright streak where the glass catches the sun
    ctx.fillRect(gx, gy, gw, 2);
    ctx.fillStyle = '#ddd7ca'; // glazing bars
    ctx.fillRect(gx + Math.floor(gw / 2) - 1, gy, 2, gh);
    ctx.fillRect(gx, gy + Math.floor(gh / 2) - 1, gw, 2);
  };
}

/** 0 — a single tall punched window with a keystone. */
const drawSingle: Draw = (ctx) => {
  brickBase(ctx);
  punchedWindow(ctx, 44, 28, 40, 76, darkGlass(ctx));
  ctx.fillStyle = '#e8e2d5'; // keystone over the head
  ctx.fillRect(59, 20, 10, 8);
};

/** 1 — a pair of narrow windows sharing one bay. */
const drawDouble: Draw = (ctx) => {
  brickBase(ctx);
  punchedWindow(ctx, 18, 30, 36, 72, darkGlass(ctx));
  punchedWindow(ctx, 74, 30, 36, 72, darkGlass(ctx));
};

/** 2 — a wide window split into two lights by a stone mullion, flanked by pilasters. */
const drawWide: Draw = (ctx) => {
  brickBase(ctx);
  // Shallow pilasters up the sides of the bay.
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  ctx.fillRect(2, 10, 7, CELL - 10);
  ctx.fillRect(CELL - 9, 10, 7, CELL - 10);
  ctx.fillStyle = 'rgba(0,0,0,0.10)';
  ctx.fillRect(9, 10, 3, CELL - 10);
  ctx.fillRect(CELL - 12, 10, 3, CELL - 10);

  punchedWindow(ctx, 24, 30, 80, 70, (gx, gy, gw, gh) => {
    const half = Math.floor(gw / 2);
    darkGlass(ctx)(gx, gy, half - 4, gh);
    darkGlass(ctx)(gx + half + 4, gy, gw - half - 4, gh);
    ctx.fillStyle = '#d6d0c3'; // stone mullion between the lights
    ctx.fillRect(gx + half - 4, gy - 1, 8, gh + 2);
  });
};

/** 3 — a tall window under a transom light. */
const drawTransom: Draw = (ctx) => {
  brickBase(ctx);
  punchedWindow(ctx, 42, 22, 44, 22, darkGlass(ctx)); // transom
  punchedWindow(ctx, 42, 54, 44, 54, darkGlass(ctx)); // main sash
};

const FACADE_DRAWS: readonly Draw[] = [drawSingle, drawDouble, drawWide, drawTransom];

/** Pack the styles into a single layered texture — one texture bind for all facades. */
function makeFacadeArray(): DataArrayTexture {
  const depth = FACADE_STYLE_COUNT;
  const data = new Uint8Array(CELL * CELL * depth * 4);

  const canvas = document.createElement('canvas');
  canvas.width = CELL;
  canvas.height = CELL;
  const ctx = canvas.getContext('2d');

  for (let layer = 0; layer < depth; layer += 1) {
    const draw = FACADE_DRAWS[layer % FACADE_DRAWS.length];
    if (ctx && draw) {
      ctx.clearRect(0, 0, CELL, CELL);
      draw(ctx);
      data.set(ctx.getImageData(0, 0, CELL, CELL).data, layer * CELL * CELL * 4);
    } else {
      // No 2D context (headless): fill neutral grey (0.5) so the overlay leaves buildings at
      // their plain brick colour rather than turning white — the tests only assert geometry.
      data.fill(128, layer * CELL * CELL * 4, (layer + 1) * CELL * CELL * 4);
    }
  }

  const tex = new DataArrayTexture(data, CELL, CELL, depth);
  tex.format = RGBAFormat;
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.magFilter = LinearFilter; // the cell is detailed enough that nearest just looks blocky
  // Mipmaps matter here: a storey is ~10 px on screen a block away, and without them the 1 px
  // mortar lines and glazing bars alias into crawling noise as the camera moves.
  tex.minFilter = LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 4; // walls are viewed at glancing angles almost all the time
  tex.colorSpace = SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * The building material samples the facade array by a per-vertex layer index. Three's
 * MeshLambertMaterial has no notion of a texture array, so this hooks its shader:
 *   • a 1×1 dummy `map` turns on the UV pipeline (so `vMapUv` exists),
 *   • the vertex stage forwards `aTexLayer` → `vTexLayer`,
 *   • the fragment stage replaces the normal map sample with an array sample at that layer.
 * The shadow depth material doesn't sample the facade (buildings are opaque), so shadows are
 * untouched.
 */
/** GLSL prologue + discard line for the hide-one-building uniform (see setHiddenFeature). */
const FEATURE_VERT_DECL = 'attribute float aFeature;\nvarying float vFeature;\n';
const FEATURE_FRAG_DECL = 'uniform float uHideFeature;\nvarying float vFeature;\n';
const FEATURE_DISCARD = 'if (abs(vFeature - uHideFeature) < 0.5) discard;';

function makeBuildingMaterial(
  facadeArray: DataArrayTexture,
  hideUniform: { value: number },
): MeshLambertMaterial {
  const dummy = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, RGBAFormat);
  dummy.needsUpdate = true;

  const material = new MeshLambertMaterial({ vertexColors: true, side: FrontSide, map: dummy });

  material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms['facadeArray'] = { value: facadeArray };
    shader.uniforms['uHideFeature'] = hideUniform;

    shader.vertexShader =
      'attribute float aTexLayer;\nvarying float vTexLayer;\n' + FEATURE_VERT_DECL +
      shader.vertexShader.replace(
        '#include <uv_vertex>',
        '#include <uv_vertex>\n  vTexLayer = aTexLayer;\n  vFeature = aFeature;',
      );

    shader.fragmentShader =
      'precision highp sampler2DArray;\nuniform sampler2DArray facadeArray;\nvarying float vTexLayer;\n' +
      FEATURE_FRAG_DECL +
      shader.fragmentShader.replace(
        '#include <map_fragment>',
        [
          FEATURE_DISCARD,
          // The cell is authored around neutral grey (0.5): overlay it onto the brick so grey
          // leaves the colour untouched, darker pixels recess to glass, and lighter pixels lift
          // to pale stone trim. Overlay is done in the texel's own (sRGB) space — its 0.5 pivot
          // is what keeps the neutral wall neutral — so the texel is NOT linearised first.
          'vec3 fTex = texture(facadeArray, vec3(vMapUv, vTexLayer)).rgb;',
          'vec3 lo = 2.0 * diffuseColor.rgb * fTex;',
          'vec3 hi = 1.0 - 2.0 * (1.0 - diffuseColor.rgb) * (1.0 - fTex);',
          'diffuseColor.rgb = mix(lo, hi, step(vec3(0.5), fTex));',
        ].join('\n  '),
      );
  };

  // A stable cache key so materials with different injected shaders never share a program.
  material.customProgramCacheKey = () => 'gf-facade-array';
  material.userData['dummyMap'] = dummy;
  return material;
}

/** Roofs get only the feature discard — no facade array on a roof. */
function makeRoofMaterial(hideUniform: { value: number }): MeshLambertMaterial {
  const material = new MeshLambertMaterial({ vertexColors: true, side: DoubleSide });
  material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms['uHideFeature'] = hideUniform;
    shader.vertexShader =
      FEATURE_VERT_DECL +
      shader.vertexShader.replace('#include <uv_vertex>', '#include <uv_vertex>\n  vFeature = aFeature;');
    shader.fragmentShader =
      FEATURE_FRAG_DECL +
      shader.fragmentShader.replace('#include <map_fragment>', `${FEATURE_DISCARD}\n#include <map_fragment>`);
  };
  material.customProgramCacheKey = () => 'gf-roof-feature';
  return material;
}

export function createMaterials(): LayerMaterials {
  const facadeArray = makeFacadeArray();
  // One shared uniform: writing -1 shows every building, an id hides that building in both the
  // wall and roof programs at once.
  const hideUniform = { value: -1 };
  const buildings = makeBuildingMaterial(facadeArray, hideUniform);

  // Roofs are a separate mesh precisely so they do not get the window texture. DoubleSide
  // because the winding fix leaves roof triangles facing down; their vertex normals already
  // point up, so lighting is correct regardless of which face draws.
  const roofs = makeRoofMaterial(hideUniform);

  // Ground layers are single-sided quads that can be seen from below when the camera dips
  // toward the horizon, so they render both faces.
  const roads = new MeshLambertMaterial({ vertexColors: true, side: DoubleSide });
  /*
   * Landuse and water opt OUT of the depth buffer entirely. Ground cover is coplanar by
   * nature — a park sits inside a suburb inside a paddock, the basemap and the survey overlap
   * at boundaries, tile joins double the odd strip — and coplanar surfaces under a moving
   * camera are exactly what z-fighting is. Painter's order replaces depth for these layers:
   * meshes carry an explicit renderOrder (context < far ring < near ring, landuse < water),
   * polygons within a mesh keep their packing order (base cover first, parks last), and
   * buildings/roads draw after with real depth, covering them as before. Flicker becomes
   * geometrically impossible instead of merely unlikely.
   */
  const landuse = new MeshLambertMaterial({
    vertexColors: true,
    side: DoubleSide,
    depthWrite: false,
    depthTest: false,
  });
  const water = new MeshLambertMaterial({
    vertexColors: true,
    side: DoubleSide,
    emissive: 0x0d2740,
    emissiveIntensity: 0.4,
    depthWrite: false,
    depthTest: false,
  });

  /*
   * Trees are solid, closed, and the only layer whose geometry is genuinely three-dimensional
   * without being architecture. FrontSide, unlike the ground layers: a canopy seen from inside
   * should not draw, and single-sided halves the fragments for the densest layer in the tile.
   */
  const trees = new MeshLambertMaterial({ vertexColors: true, side: FrontSide });

  return {
    buildings,
    roofs,
    roads,
    water,
    landuse,
    trees,
    setHiddenFeature(osmWayId: number | null): void {
      hideUniform.value = osmWayId === null ? -1 : osmWayId % 16777216;
    },
    dispose(): void {
      facadeArray.dispose();
      (buildings.userData['dummyMap'] as Texture | undefined)?.dispose();
      buildings.dispose();
      roofs.dispose();
      roads.dispose();
      water.dispose();
      landuse.dispose();
      trees.dispose();
    },
  };
}

export function materialFor(materials: LayerMaterials, layer: string): Material {
  switch (layer) {
    case 'buildings': return materials.buildings;
    case 'roofs': return materials.roofs;
    case 'roads': return materials.roads;
    case 'water': return materials.water;
    case 'trees': return materials.trees;
    default: return materials.landuse;
  }
}

/** Layers that cast shadows. Ground-plane layers only receive them. */
export function castsShadow(layer: string): boolean {
  return layer === 'buildings' || layer === 'roofs' || layer === 'trees';
}
